/**
 * Keys of the catalog page cache (window.__vf_catalog_state_cache plus its
 * sessionStorage copy in pages/store/category/category.vue).
 *
 * A key names one result page: its query variables (which carry the display
 * page), the listing URL's other query parameters and everything that changes
 * prices or the result set for the shopper. The URL's own `?page` is the page
 * the shopper is on, not the page being stored, so it is left out: page 2
 * must have the same key whether it is requested from page 1 (View Next),
 * from page 3 (View Previous) or by Back/Forward. Including it made View
 * Previous and Back 3->2 miss the cache every time.
 */

export type CatalogRouteQuery = Record<string, unknown>;

export type CatalogPageCacheKeyInput = {
  version: string;
  categoryId: string;
  variables: Record<string, unknown>;
  routeQuery: CatalogRouteQuery;
  source: "legacy" | "catalogSearchV1";
  currency: string;
  locale: string;
  customer: string;
  // Raw customer ids (number or string), kept as the API returns them.
  customerGroup: unknown;
  taxAddress: unknown;
};

/** Query parameters that never belong to a cache key. */
const ROUTE_QUERY_KEYS_OUTSIDE_THE_KEY = new Set(["page"]);

/**
 * The listing URL's query as it enters a page cache key: sorted keys, array
 * values sorted, undefined values dropped and `page` left out.
 */
export const catalogPageCacheRouteQuery = (
  query: CatalogRouteQuery | null | undefined
): Record<string, string | string[]> =>
  Object.fromEntries(
    Object.keys(query || {})
      .sort()
      .filter(
        (key) =>
          !ROUTE_QUERY_KEYS_OUTSIDE_THE_KEY.has(key) &&
          (query as CatalogRouteQuery)[key] !== undefined
      )
      .map((key) => {
        const value = (query as CatalogRouteQuery)[key];
        return [
          key,
          Array.isArray(value)
            ? value.map(String).sort()
            : value == null
            ? ""
            : String(value),
        ];
      })
  );

const encodeKeyPayload = (payload: string) =>
  btoa(unescape(encodeURIComponent(payload)));

/**
 * Builds the page cache key. The browser key is base64 encoded (it is also a
 * sessionStorage key); the server never stores pages, so it keeps the JSON.
 */
export const buildCatalogPageCacheKey = (
  input: CatalogPageCacheKeyInput,
  encode = typeof window !== "undefined"
): string => {
  const variables: Record<string, unknown> = {};
  Object.keys(input.variables || {})
    .sort()
    .forEach((key) => (variables[key] = input.variables[key]));
  const payload = JSON.stringify({
    variables,
    routeQuery: catalogPageCacheRouteQuery(input.routeQuery),
    source: input.source,
    currency: input.currency,
    locale: input.locale,
    customer: input.customer,
    customerGroup: input.customerGroup,
    taxAddress: input.taxAddress,
  });
  const encoded = encode ? encodeKeyPayload(payload) : payload;
  return `category_state_${input.version}_${input.categoryId}_${encoded}`;
};

/**
 * Most pages the in-memory page cache keeps. Pages, scroll and intent
 * prefetches and the leave snapshots all add entries, and expired ones go
 * only when they are read again, so without a cap the Map grew for the whole
 * visit. 32 pages cover every page of the largest listing twice over within
 * the 5-minute TTL; sessionStorage keeps its own last 4.
 */
export const CATALOG_STATE_CACHE_MAX_ENTRIES = 32;

/**
 * Stores `entry` as the most recent one and drops the oldest entries above
 * `maxEntries` (Map order is insertion order, refreshed on every store).
 */
export const rememberCatalogPageEntry = <T>(
  cache: Map<string, T>,
  key: string,
  entry: T,
  maxEntries = CATALOG_STATE_CACHE_MAX_ENTRIES
) => {
  cache.delete(key);
  cache.set(key, entry);
  while (cache.size > Math.max(1, maxEntries)) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
};

/**
 * When a stored page was fetched. An entry stored again (the leave snapshot,
 * a canonical-URL copy) keeps the time its products came from the server, so
 * re-storing never extends the TTL of old prices and stock.
 */
export const catalogEntryCreated = (created: unknown, now = Date.now()) => {
  const value = Number(created);
  return Number.isFinite(value) && value > 0 && value <= now ? value : now;
};
